import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { aiAssistantSaga } from './saga';
import type { RoadmapContext } from '../exposes/AIAssistant';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  status: 'complete' | 'streaming' | 'error';
  requestId?: string;
  thinkingText?: string;
}

export interface AIAssistantState {
  messages: ChatMessage[];
  conversationId: string | null;
  runtimeSessionId: string | null;
  isStreaming: boolean;
  error: string | null;
  activeTool: string | null;
  thinkingText: string | null;
  externalContext: RoadmapContext;
}

export const initialState: AIAssistantState = {
  messages: [],
  conversationId: null,
  runtimeSessionId: null,
  isStreaming: false,
  error: null,
  activeTool: null,
  thinkingText: null,
  externalContext: {}
};

const aiAssistantSlice = createSlice({
  name: 'aiAssistant',
  initialState,
  reducers: {
    sendMessage(state, action: PayloadAction<string>) {
      state.messages.push({
        id: crypto.randomUUID(),
        role: 'user',
        content: action.payload,
        timestamp: Date.now(),
        status: 'complete'
      });
      state.isStreaming = true;
      state.error = null;
    },
    streamStart(state, action: PayloadAction<{ messageId: string }>) {
      state.messages.push({
        id: action.payload.messageId,
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        status: 'streaming'
      });
    },
    setConversationId(state, action: PayloadAction<string>) {
      state.conversationId = action.payload;
    },
    setRuntimeSessionId(state, action: PayloadAction<string>) {
      state.runtimeSessionId = action.payload;
    },
    appendToken(
      state,
      action: PayloadAction<{ messageId: string; token: string }>
    ) {
      const msg = state.messages.find(m => m.id === action.payload.messageId);
      if (msg) {
        msg.content += action.payload.token;
      }
    },
    setActiveTool(state, action: PayloadAction<string | null>) {
      state.activeTool = action.payload;
    },
    setThinkingText(state, action: PayloadAction<string | null>) {
      state.thinkingText = action.payload;
      if (action.payload !== null) {
        const streamingMsg = state.messages.find(m => m.status === 'streaming');
        if (streamingMsg) {
          streamingMsg.thinkingText = action.payload;
        }
      }
    },
    setMessageRequestId(
      state,
      action: PayloadAction<{ messageId: string; requestId: string }>
    ) {
      const msg = state.messages.find(m => m.id === action.payload.messageId);
      if (msg) {
        msg.requestId = action.payload.requestId;
      }
    },
    streamComplete(state, action: PayloadAction<string>) {
      const msg = state.messages.find(m => m.id === action.payload);
      if (msg) {
        msg.status = 'complete';
      }
      state.isStreaming = false;
      state.activeTool = null;
      state.thinkingText = null;
    },
    streamError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.isStreaming = false;
      state.activeTool = null;
      state.thinkingText = null;
      const streamingMsg = state.messages.find(m => m.status === 'streaming');
      if (streamingMsg) {
        streamingMsg.status = 'error';
      }
    },
    newConversation(state) {
      state.messages = [];
      state.conversationId = null;
      state.runtimeSessionId = null;
      state.error = null;
      state.isStreaming = false;
      state.activeTool = null;
      state.thinkingText = null;
      // externalContext is set by AiAssistantSection (slideout) or AIAssistantDock (overlay)
      // state (open RSD, filters, etc.) — it must not be reset here or context is
      // permanently lost until something triggers the setExternalContext effect again.
    },
    setExternalContext(state, action: PayloadAction<RoadmapContext>) {
      state.externalContext = action.payload;
    },
    cancelStream(_state) {
      // saga handles cancellation; state is updated via streamComplete dispatch
    }
  }
});

export const aiAssistantActions = aiAssistantSlice.actions;
export const aiAssistantReducer = aiAssistantSlice.reducer;

export const useAiAssistantSlice = () => {
  useInjectReducer({
    key: aiAssistantSlice.name,
    reducer: aiAssistantSlice.reducer
  });
  useInjectSaga({ key: aiAssistantSlice.name, saga: aiAssistantSaga });
  return { actions: aiAssistantSlice.actions };
};
